import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { GET_ALLOCATION } from "../graphql/allocation";
import CustomFieldType from "enums/CustomFieldType";
import {
  CustomFieldsRecord,
  FormikCustomField,
} from "hooks/customFields.types";

type AllocationMap = Record<string, string>;

type AllocationLookup = {
  allocationById: { allocationCode?: string | null } | null;
};

const useCustomFieldAllocation = (
  customFieldSettings: FormikCustomField[],
  customFields?: CustomFieldsRecord,
): {
  allocationMap: AllocationMap;
  loading: boolean;
  /** True until the id's lookup has settled, found or not. */
  isResolving: (id: string) => boolean;
} => {
  const client = useApolloClient();
  const [allocationMap, setAllocationMap] = useState<AllocationMap>({});
  const [settled, setSettled] = useState<ReadonlySet<string>>(() => new Set());
  const requestedRef = useRef(new Set<string>());

  // Extract all unique allocation IDs from the custom fields
  const allocationIds = useMemo(() => {
    if (!customFields) return [];

    const ids = new Set<string>();

    customFieldSettings.forEach((field) => {
      if (field.type !== CustomFieldType.ALLOCATION) return;

      const values = customFields[field.id!] ?? [];
      values.forEach((id) => {
        if (id) {
          ids.add(id);
        }
      });
    });

    return Array.from(ids);
  }, [customFieldSettings, customFields]);

  const allocationIdsKey = JSON.stringify(allocationIds.sort());

  // NOTE FOR FUTURE : this currently will only even have one id in the array so we don't actually use the loop, its for future proofing if
  // we ever want to add more than one allocation per custom field. Also if you're coming here to add that, just add batch get id's on the backend :)
  useEffect(() => {
    allocationIds
      .filter((id) => !requestedRef.current.has(id))
      .forEach((id) => {
        requestedRef.current.add(id);
        const lookup = { query: GET_ALLOCATION, variables: { id } };
        const cached =
          client.readQuery<AllocationLookup>(lookup)?.allocationById
            ?.allocationCode;
        if (cached) {
          setAllocationMap((prev) => ({ ...prev, [id]: cached }));
          setSettled((prev) => new Set(prev).add(id));
        }
        // One query per id; a shared lazy query can mix up concurrent lookups.
        client
          .query<AllocationLookup>({
            ...lookup,
            // UPDATE_ALLOCATION returns only the id, so a cached code can be stale.
            fetchPolicy: "network-only",
          })
          .then(({ data }) => {
            const code = data?.allocationById?.allocationCode;
            setAllocationMap((prev) => {
              const next = { ...prev };
              if (code) next[id] = code;
              else delete next[id];
              return next;
            });
          })
          .catch((error) => {
            console.error(`Failed to fetch allocation for ID: ${id}`, error);
          })
          .finally(() => setSettled((prev) => new Set(prev).add(id)));
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allocationIdsKey]);

  const isResolving = useCallback((id: string) => !settled.has(id), [settled]);

  return {
    allocationMap,
    loading: allocationIds.some(isResolving),
    isResolving,
  };
};

export default useCustomFieldAllocation;
